/**
 * ISSUE-1358. The Messages screen's "New message" entry, after v1's
 * `add_message.dart`: a username search, a `New group` row, and the
 * `Suggested` people messaging-api returns (recent one-to-one partners first,
 * then pool-mates, from `/chat/list-suggested-people`).
 *
 * Typing narrows the suggestions on the server (a prefix of a username or a
 * name word) and, once the text is a complete username, also looks it up
 * exactly through `/profile/search-by-username`. Strangers are findable by
 * their exact username only (profile rule 12), so the two reads stay apart.
 *
 * Tapping a person goes through `useOpenPrivateChat`, the same path a fan
 * profile's Message button uses, so a first message to a new person still
 * goes through the request flow (messaging overview rule 11).
 */
import { useChatListSuggestedPeople } from "@sportsmet/messaging-sdk/private";
import { useProfileSearchByUsername } from "@sportsmet/profile-sdk/private";
import { Dialog, FOCUS_RING, Input, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  normalizeUsername,
  USERNAME_PATTERN,
} from "../editProfile/editProfileLogic";
import { ChatAvatar } from "./ChatAvatar";
import {
  NEW_MESSAGE_GROUP_HINT,
  NEW_MESSAGE_GROUP_LABEL,
  NEW_MESSAGE_SEARCH_LABEL,
  NEW_MESSAGE_SEARCH_PLACEHOLDER,
  NEW_MESSAGE_SELF_MESSAGE,
  NEW_MESSAGE_SUGGESTED_EMPTY,
  NEW_MESSAGE_SUGGESTED_NO_MATCH,
  NEW_MESSAGE_SUGGESTED_TITLE,
  NEW_MESSAGE_TITLE,
  newMessageToLabel,
} from "./chatListActionsCopy";
import {
  NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE,
  NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL,
  NEW_CHAT_ADD_PEOPLE_START_MESSAGE,
} from "./standaloneChatCopy";
import { useOpenPrivateChat } from "./useOpenPrivateChat";

/** The `?dialog=` value that opens this dialog on `/messages`. */
export const NEW_MESSAGE_DIALOG = "new-message";

/** How many suggested people the dialog asks for. */
export const NEW_MESSAGE_SUGGESTED_LIMIT = 20;

/** How long typing has to pause before the suggestions narrow. */
const SUGGESTED_QUERY_DEBOUNCE_MS = 250;

type typePerson = {
  userId: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
};

type typeSearchPhase = "idle" | "searching" | "found" | "self" | "notFound";

const ROW_CLASS = `flex w-full min-h-touch-target items-center gap-3 rounded-md py-2 text-left hover:bg-surface-secondary ${FOCUS_RING}`;

export function NewMessageDialog({
  open,
  onOpenChange,
  onNewGroup,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewGroup: () => void;
}): ReactNode {
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : null;
  const [usernameQuery, setUsernameQuery] = useState("");
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);
  const { openPrivateChat } = useOpenPrivateChat();

  const normalizedQuery = normalizeUsername(usernameQuery).replace(/^@/, "");
  const isQueryComplete = USERNAME_PATTERN.test(normalizedQuery);

  const [suggestedQuery, setSuggestedQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSuggestedQuery(normalizedQuery);
    }, SUGGESTED_QUERY_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [normalizedQuery]);
  const suggested = useChatListSuggestedPeople(
    {
      limit: NEW_MESSAGE_SUGGESTED_LIMIT,
      ...(suggestedQuery !== "" ? { query: suggestedQuery } : {}),
    },
    { enabled: open },
  );
  const search = useProfileSearchByUsername(
    { username: normalizedQuery },
    { enabled: open && isQueryComplete },
  );

  // Same split as NewChatDialog: a failed read raises the offline modal, an
  // error envelope raises the API error modal, a miss is just "not found".
  useEffect(() => {
    if (!isQueryComplete) return;
    if (search.isError) {
      showOfflineError();
      return;
    }
    if (extractErrorCode(search.data) !== null) {
      showApiError(search.data, undefined, search.dataUpdatedAt);
    }
  }, [isQueryComplete, normalizedQuery, search.isError, search.dataUpdatedAt]);

  const hit = search.data?.data?.items[0];
  let phase: typeSearchPhase;
  if (
    !isQueryComplete ||
    search.isError ||
    extractErrorCode(search.data) !== null
  ) {
    phase = "idle";
  } else if (search.isLoading) {
    phase = "searching";
  } else if (hit === undefined) {
    phase = "notFound";
  } else if (hit.userId === viewerUserId) {
    phase = "self";
  } else {
    phase = "found";
  }

  // Closing drops the typed username, the same as NewChatDialog's draft.
  const handleOpenChange = (next: boolean): void => {
    if (!next) setUsernameQuery("");
    onOpenChange(next);
  };

  const suggestedPeople: typePerson[] = (suggested.data?.data?.items ?? []).map(
    (item) => ({
      userId: item.userId,
      displayName: item.displayName,
      username: item.username,
      avatarUrl: item.avatarUrl ?? null,
    }),
  );
  // The exact hit already sits in Suggested when the fan knows them, so the
  // search result only draws for someone the list does not have.
  const isHitSuggested =
    hit !== undefined &&
    suggestedPeople.some((person) => person.userId === hit.userId);
  const isSuggestedLoading = suggested.isLoading;
  const hasSuggestedFailed =
    suggested.isError || extractErrorCode(suggested.data) !== null;

  const openPerson = (userId: string): void => {
    if (openingUserId !== null) return;
    setOpeningUserId(userId);
    void openPrivateChat(userId).then((opened) => {
      setOpeningUserId(null);
      if (opened) setUsernameQuery("");
    });
  };

  const personRow = (person: typePerson, testId: string): ReactNode => (
    <button
      type="button"
      data-testid={testId}
      aria-label={newMessageToLabel(person.displayName)}
      aria-busy={openingUserId === person.userId}
      disabled={openingUserId === person.userId}
      className={`${ROW_CLASS} disabled:opacity-60`}
      onClick={() => {
        openPerson(person.userId);
      }}
    >
      <ChatAvatar title={person.displayName} photoUrl={person.avatarUrl} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-strong text-ink">
          {person.displayName}
        </span>
        <span className="truncate text-caption text-ink-secondary">
          @{person.username}
        </span>
      </span>
    </button>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={NEW_MESSAGE_TITLE}
    >
      <div className="flex flex-col gap-4">
        <Input
          type="search"
          label={NEW_MESSAGE_SEARCH_LABEL}
          placeholder={NEW_MESSAGE_SEARCH_PLACEHOLDER}
          autoComplete="off"
          data-testid="new-message-search"
          value={usernameQuery}
          onChange={(event) => {
            setUsernameQuery(event.target.value);
          }}
        />

        <div className="flex flex-col gap-2" aria-live="polite">
          {phase === "idle" ? (
            <p className="text-body text-ink-secondary">
              {NEW_CHAT_ADD_PEOPLE_START_MESSAGE}
            </p>
          ) : null}
          {phase === "searching" ? (
            <div data-testid="new-message-searching">
              <Skeleton
                shape="control"
                label={NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL}
              />
            </div>
          ) : null}
          {phase === "notFound" ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="new-message-not-found"
            >
              {NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE}
            </p>
          ) : null}
          {phase === "self" ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="new-message-self"
            >
              {NEW_MESSAGE_SELF_MESSAGE}
            </p>
          ) : null}
          {phase === "found" && hit !== undefined && !isHitSuggested
            ? personRow(
                {
                  userId: hit.userId,
                  displayName: hit.displayName,
                  username: hit.username,
                  avatarUrl: hit.avatarUrl ?? null,
                },
                `new-message-result-${hit.userId}`,
              )
            : null}
        </div>

        <button
          type="button"
          data-testid="new-message-group"
          className={ROW_CLASS}
          onClick={onNewGroup}
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-tint text-body-strong text-primary"
          >
            +
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-body-strong text-ink">
              {NEW_MESSAGE_GROUP_LABEL}
            </span>
            <span className="text-caption text-ink-secondary">
              {NEW_MESSAGE_GROUP_HINT}
            </span>
          </span>
        </button>

        <section className="flex flex-col gap-1">
          <h3 className="text-caption text-ink-secondary">
            {NEW_MESSAGE_SUGGESTED_TITLE}
          </h3>
          {isSuggestedLoading ? (
            <div
              className="flex flex-col gap-2"
              data-testid="new-message-suggested-loading"
            >
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : hasSuggestedFailed ? null : suggestedPeople.length === 0 ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="new-message-suggested-empty"
            >
              {suggestedQuery === ""
                ? NEW_MESSAGE_SUGGESTED_EMPTY
                : NEW_MESSAGE_SUGGESTED_NO_MATCH}
            </p>
          ) : (
            <ul className="flex flex-col" data-testid="new-message-suggested">
              {suggestedPeople.map((person) => (
                <li key={person.userId}>
                  {personRow(person, `new-message-suggested-${person.userId}`)}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Dialog>
  );
}
